export type ToastType = 'success' | 'error' | 'info' | 'warning';

class ToastManager {
  private container: HTMLElement;

  constructor() {
    this.container = document.getElementById('toast-container') || this.createContainer();
  }

  private createContainer(): HTMLElement {
    const el = document.createElement('div');
    el.id = 'toast-container';
    el.className = 'fixed top-12 left-1/2 -translate-x-1/2 z-[9999] flex flex-col gap-3 pointer-events-none w-full max-w-xs items-center px-4 font-sans';
    document.body.appendChild(el);
    return el;
  }

  show(message: string, type: ToastType = 'info', duration: number = 3200) {
    const toast = document.createElement('div');
    toast.className = `flex-shrink-0 animate-in fade-in slide-in-from-top-4 duration-300 pointer-events-auto shadow-2xl`;
    
    const icon = this.getIcon(type);
    let accentClasses = '';
    let textClass = 'text-white';
    
    if (type === 'success') {
      accentClasses = 'bg-emerald-600 border-emerald-500';
    } else if (type === 'error') {
      accentClasses = 'bg-red-600 border-red-500';
    } else if (type === 'warning') {
      accentClasses = 'bg-amber-500 border-amber-400';
      textClass = 'text-zinc-950 font-bold';
    } else {
      accentClasses = 'bg-slate-900 border-slate-700';
    }
    
    toast.innerHTML = `
      <div class="flex items-center gap-3 p-4 border ${accentClasses} rounded-2xl min-w-[280px] max-w-sm backdrop-blur-md">
        <div class="flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center bg-white/20">
           ${icon}
        </div>
        <div class="flex-1 min-w-0 text-left">
           <h4 class="text-[9px] font-black uppercase tracking-widest leading-none opacity-60 mb-0.5">${type}</h4>
           <p class="text-[12px] font-bold ${textClass} leading-tight">${message}</p>
        </div>
      </div>
    `;

    this.container.appendChild(toast);

    setTimeout(() => {
      toast.classList.add('animate-out', 'fade-out', 'slide-out-to-top-10');
      setTimeout(() => toast.remove(), 300);
    }, duration);
  }

  private getIcon(type: ToastType): string {
    const color = type === 'warning' ? '#000000' : '#ffffff';
    switch (type) {
      case 'success':
        return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`;
      case 'error':
        return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>`;
      case 'warning':
        return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`;
      default:
        return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>`;
    }
  }
}

export const toast = new ToastManager();
